/*
 * Copyright (c) 2026. Tooling Team
 */

import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { formatLongDescription } from "../components/table/LongDescriptionCellRenderer";
import Layout from "../components/layout/Layout";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Card } from "@uhg-abyss/web/ui/Card";
import { PageBodyIntro } from "@uhg-abyss/web/ui/PageBodyIntro";
import { PageBody } from "@uhg-abyss/web/ui/PageBody";
import { Text } from "@uhg-abyss/web/ui/Text";
import { RouteDisplay } from "../components/navigation/RouteDisplay";
import { PATHS } from "../utils/constants";
import { Tabs } from "@uhg-abyss/web/ui/Tabs";
import { TextInput } from "@uhg-abyss/web/ui/TextInput";
import {
  useCreateClientMutation,
  useGetClientListQuery,
  useRetireClientMutation,
  useUpdateClientMutation,
} from "../app/services/clientApi";
import { useState, useMemo, useEffect } from "react";
import { FileUpload } from "@uhg-abyss/web/ui/FileUpload";
import { FormProvider } from "@uhg-abyss/web/ui/FormProvider";
import { useForm } from "@uhg-abyss/web/hooks/useForm";
import { useToast } from "@uhg-abyss/web/hooks/useToast";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { DataTable } from "@uhg-abyss/web/ui/DataTable";
import { useDataTable } from "@uhg-abyss/web/hooks/useDataTable";
import { downloadCsv } from "@uhg-abyss/web/tools/downloadCsv";
import { Checkbox } from "@uhg-abyss/web/ui/Checkbox";
import { useSelector } from "react-redux";
import {
  selectSystemDomainValues,
  useCreateConceptSetMutation,
  useGetConceptSetsQuery,
  useSearchConceptSetsByConditionQuery,
  useUpdateConceptSetMutation,
} from "../app/services/conceptSetsApi";
import { getSelectedObject } from "../utils/funcs";
import { isAdminUser } from "../utils/authz";
import {
  getClientIdByName,
  getClientNameDropdownValues,
} from "../utils/clientDropdown";

export const VIEW_CONCEPT_SET_COLUMNS = [
  { header: "Code", accessorKey: "code" },
  { header: "Description", accessorKey: "description" },
  {
    header: "Long Description",
    accessorKey: "longDescription",
    enableColumnFilter: false,
    enableSorting: false,
    size: 120,
    minSize: 120,
    maxSize: 120,
    cell: (cell) => {
      const longDesc = cell.getValue();
      if (!longDesc) return null;
      const description = cell.row.original.description || "None";
      const popover = (
        <Popover id="concept-long-desc-popover">
          <Popover.Header as="h3">Description</Popover.Header>
          <Popover.Body style={{ maxWidth: "350px" }}>
            {description}
          </Popover.Body>
          <Popover.Header as="h3">JSON Data</Popover.Header>
          <Popover.Body style={{ maxWidth: "350px" }}>
            <span>{formatLongDescription(longDesc)}</span>
          </Popover.Body>
        </Popover>
      );
      return (
        <div
          className="localDescIcon mt-2andaBit fa-lg localDescIconCustom"
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <OverlayTrigger
            trigger={["hover", "focus"]}
            placement="right"
            overlay={popover}
          >
            <div>
              <FontAwesomeIcon icon={regular("rectangle-list")} />
            </div>
          </OverlayTrigger>
        </div>
      );
    },
  },
  { header: "System", accessorKey: "system" },
];

export const DOWNLOAD_CONCEPT_SET_COLUMNS = [
  { header: "CODE", accessorKey: "code" },
  { header: "DESCRIPTION", accessorKey: "description" },
  { header: "CONCEPT_SET", accessorKey: "conceptSet" },
  { header: "LONG_DESCRIPTION", accessorKey: "longDescription" },
  { header: "SYSTEM", accessorKey: "system" },
  { header: "VERSION", accessorKey: "version" },
];

const INITIAL_CREATE_CLIENT_FORM_VALUES = {
  clientName: "",
  description: "",
};

const INITIAL_EDIT_CLIENT_FORM_VALUES = {
  selectedClientName: "",
  clientName: "",
  description: "",
};

export const getConceptDomainDropdownValues = (conceptSetsData = []) => {
  const domainsByName = new Map();

  conceptSetsData.forEach((concept) => {
    if (!domainsByName.has(concept.domain)) {
      domainsByName.set(concept.domain, concept.domain);
    }
  });

  return Array.from(domainsByName.values())
    .sort((a, b) => a.localeCompare(b))
    .map((domain, index) => ({
      id: index,
      label: domain,
      value: domain,
    }));
};

export const getConceptSystemDropdownValues = (
  conceptSetsData = [],
  selectedDomain
) => {
  if (!selectedDomain) {
    return [];
  }

  return conceptSetsData
    .filter((concept) => concept.domain === selectedDomain.value)
    .map((concept, index) => ({
      id: index,
      label: concept.system,
      value: concept.conceptSetId,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const buildEditConceptSetUpdateRequest = (
  file,
  selectedDomain,
  selectedSystem
) => ({
  conceptSetId: selectedSystem.value,
  payload: {
    csvFile: file,
    domain: selectedDomain.value,
    codeSystem: selectedSystem.label,
  },
});

export const isEditConceptSetSelectionComplete = (
  selectedDomain,
  selectedSystem
) =>
  Boolean(
    selectedDomain?.value && selectedSystem?.value && selectedSystem?.label
  );

export const buildConceptSetSearchCondition = (
  selectedDomain,
  selectedSystem
) => {
  if (!selectedDomain?.value || !selectedSystem?.label) return null;

  return {
    criteria: {
      searchExpression: [
        {
          and: [
            { eq: { name: `${selectedDomain.value}_conceptset` } },
            { eq: { system: selectedSystem.label } },
          ],
        },
      ],
    },
  };
};

export const buildConceptSetDownloadRows = (rows, domainValue) =>
  rows.map((row) => ({
    ...row,
    conceptSet: domainValue,
    version: "",
  }));

// Shared by any tab that needs to search concept sets by domain/system selection
const useConceptSetSearchResults = (selectedDomain, selectedSystem) => {
  const condition = useMemo(
    () => buildConceptSetSearchCondition(selectedDomain, selectedSystem),
    [selectedDomain, selectedSystem]
  );

  const { data, isFetching } = useSearchConceptSetsByConditionQuery(condition, {
    skip: !condition,
  });

  const rows = useMemo(() => data?.[0]?.values ?? [], [data]);

  return { condition, rows, isFetching };
};

export const getConceptToastUpdate = (
  results,
  {
    isError = false,
    domainValue,
    systemValue,
    defaultSuccessMessage = "Concept set updated successfully.",
  } = {}
) => {
  if (isError) {
    return {
      title: "Error",
      type: "error",
      message:
        results?.data?.message ||
        results?.data?.detail ||
        results?.message ||
        results?.detail ||
        "Unknown error",
      autoClose: false,
    };
  }

  return {
    title: `Success for ${domainValue} and ${systemValue}`,
    type: "success",
    message:
      results?.message || results?.data?.message || defaultSuccessMessage,
    autoClose: false,
  };
};

export function AdminManagement() {
  const isAdmin = isAdminUser();

  // Create Client state and handlers
  const [createClientMutation] = useCreateClientMutation();
  const [createClientFormValues, setCreateClientFormValues] = useState(
    INITIAL_CREATE_CLIENT_FORM_VALUES
  );
  const [createClientErrors, setCreateClientErrors] = useState({});
  const [isCreateClientSubmitting, setIsCreateClientSubmitting] =
    useState(false);
  const [createClientSubmissionState, setCreateClientSubmissionState] =
    useState({
      variant: "",
      message: "",
    });

  const handleCreateClientChange = (field) => (event) => {
    const value = event.target.value;
    setCreateClientFormValues((previousState) => ({
      ...previousState,
      [field]: value,
    }));

    if (createClientErrors[field]) {
      setCreateClientErrors((previousErrors) => ({
        ...previousErrors,
        [field]: undefined,
      }));
    }
  };

  const validateCreateClientForm = () => {
    const validationErrors = {};
    const trimmedClientName = createClientFormValues.clientName
      .trim()
      .toLowerCase();

    if (!trimmedClientName) {
      validationErrors.clientName = "Client name is required.";
    }

    return validationErrors;
  };

  const resetCreateClientForm = () => {
    setCreateClientFormValues(INITIAL_CREATE_CLIENT_FORM_VALUES);
    setCreateClientErrors({});
    setCreateClientSubmissionState({
      variant: "",
      message: "",
    });
  };

  const handleCreateClientSubmit = async (event) => {
    event.preventDefault();
    setCreateClientSubmissionState({
      variant: "",
      message: "",
    });

    const validationErrors = validateCreateClientForm();
    if (Object.keys(validationErrors).length > 0) {
      setCreateClientErrors(validationErrors);
      return;
    }

    setIsCreateClientSubmitting(true);

    try {
      const requestBody = {
        name: createClientFormValues.clientName.trim().toLowerCase(),
        description: createClientFormValues.description.trim(),
      };

      await createClientMutation(requestBody).unwrap();

      setCreateClientFormValues(INITIAL_CREATE_CLIENT_FORM_VALUES);
      setCreateClientErrors({});
      setCreateClientSubmissionState({
        variant: "success",
        message: `Client ${requestBody.name} was submitted successfully.`,
      });
    } catch (error) {
      let errorMessage =
        "There was a problem creating the client. Please try again.";

      if (error.status === 409) {
        errorMessage =
          "A client with this name already exists. Please use a different name.";
      }
      setCreateClientSubmissionState({
        variant: "error",
        message: errorMessage,
      });
    } finally {
      setIsCreateClientSubmitting(false);
    }
  };

  const canCreateClientSubmit =
    createClientFormValues.clientName.trim() !== "" &&
    !isCreateClientSubmitting;

  // Edit Client state and handlers
  const [updateClientMutation] = useUpdateClientMutation();
  const [retireClientMutation] = useRetireClientMutation();
  const [editClientFormValues, setEditClientFormValues] = useState(
    INITIAL_EDIT_CLIENT_FORM_VALUES
  );
  const [editClientErrors, setEditClientErrors] = useState({});
  const [isEditClientSubmitting, setIsEditClientSubmitting] = useState(false);
  const [isRetireClient, setIsRetireClient] = useState(false);
  const [editClientSubmissionState, setEditClientSubmissionState] = useState({
    variant: "",
    message: "",
  });
  const {
    data: clientListResponse,
    isLoading: isClientListLoading = false,
    isFetching: isClientListFetching = false,
    isError: isClientListError = false,
  } = useGetClientListQuery(undefined, { skip: !isAdmin });

  const clientDropdownValues = useMemo(() => {
    return getClientNameDropdownValues(clientListResponse);
  }, [clientListResponse]);

  const handleEditClientChange = (field) => (event) => {
    const value = event.target.value;
    setEditClientFormValues((previousState) => ({
      ...previousState,
      [field]: value,
    }));

    if (editClientErrors[field]) {
      setEditClientErrors((previousErrors) => ({
        ...previousErrors,
        [field]: undefined,
      }));
    }
  };

  const validateEditClientForm = () => {
    const validationErrors = {};
    const selectedClientName = editClientFormValues.selectedClientName.trim();
    const trimmedClientName = editClientFormValues.clientName.trim();
    const trimmedDescription = editClientFormValues.description.trim();

    if (!selectedClientName) {
      validationErrors.selectedClientName = "Client is required.";
    }

    if (!isRetireClient && !trimmedClientName && !trimmedDescription) {
      validationErrors.clientName =
        "Provide a client name or description to update.";
    }

    return validationErrors;
  };

  const resetEditClientForm = () => {
    setEditClientFormValues(INITIAL_EDIT_CLIENT_FORM_VALUES);
    setEditClientErrors({});
    setIsRetireClient(false);
    setEditClientSubmissionState({
      variant: "",
      message: "",
    });
  };

  const handleEditClientSubmit = async (event) => {
    event.preventDefault();
    setEditClientSubmissionState({
      variant: "",
      message: "",
    });

    const validationErrors = validateEditClientForm();
    if (Object.keys(validationErrors).length > 0) {
      setEditClientErrors(validationErrors);
      return;
    }

    setIsEditClientSubmitting(true);

    try {
      const clientId = getClientIdByName(
        clientListResponse,
        editClientFormValues.selectedClientName
      );

      if (!clientId) {
        setEditClientErrors({
          selectedClientName: "Selected client could not be found.",
        });
        return;
      }

      if (isRetireClient) {
        await retireClientMutation({
          clientId,
          payload: { is_retired: true },
        }).unwrap();
      } else {
        const payload = {};
        const trimmedName = editClientFormValues.clientName.trim();
        const trimmedDescription = editClientFormValues.description.trim();

        if (trimmedName) {
          payload.new_name = trimmedName;
        }

        if (trimmedDescription) {
          payload.description = trimmedDescription;
        }

        await updateClientMutation({
          clientId,
          payload,
        }).unwrap();
      }

      setEditClientFormValues(INITIAL_EDIT_CLIENT_FORM_VALUES);
      setEditClientErrors({});
      setIsRetireClient(false);
      setEditClientSubmissionState({
        variant: "success",
        message: isRetireClient
          ? "Client was retired successfully."
          : "Client was updated successfully.",
      });
    } catch (error) {
      let errorMessage =
        "There was a problem updating the client. Please check the selected client and try again.";

      setEditClientSubmissionState({
        variant: "error",
        message: errorMessage,
      });
    } finally {
      setIsEditClientSubmitting(false);
    }
  };

  const canEditClientSubmit =
    editClientFormValues.selectedClientName.trim() !== "" &&
    (isRetireClient ||
      editClientFormValues.clientName.trim() !== "" ||
      editClientFormValues.description.trim() !== "") &&
    !isEditClientSubmitting;

  const handleRetireClientChange = (event) => {
    const shouldRetire = event.target.checked;
    setIsRetireClient(shouldRetire);
    if (shouldRetire) {
      setEditClientErrors((previousErrors) => ({
        ...previousErrors,
        clientName: undefined,
      }));
    }
  };

  const handleEditClientSelect = (value) => {
    setEditClientFormValues((previousState) => ({
      ...previousState,
      selectedClientName: value || "",
    }));

    if (editClientErrors.selectedClientName) {
      setEditClientErrors((previousErrors) => ({
        ...previousErrors,
        clientId: undefined,
      }));
    }
  };

  const isClientDropdownDisabled =
    isEditClientSubmitting ||
    isClientListLoading ||
    isClientListFetching ||
    clientDropdownValues.length === 0;

  // --- Concept create state and handlers ---
  const [domain, setDomain] = useState("");
  const [system, setSystem] = useState("");
  const [createConceptApi] = useCreateConceptSetMutation();
  const [isCreateConceptSubmitting, setIsCreateConceptSubmitting] =
    useState(false);
  const [isCreateConceptUploading, setIsCreateConceptUploading] =
    useState(false);
  const createConceptForm = useForm();
  const { toast } = useToast();

  const conceptFileTypesAccepted = {
    "text/csv": [".csv"],
  };

  const resetCreateConceptState = () => {
    setDomain("");
    setSystem("");
    createConceptForm.reset();
    setIsCreateConceptSubmitting(false);
    setIsCreateConceptUploading(false);
  };

  //Toast used for either edit or create concept
  const updateConceptToast = (
    results,
    {
      isError = false,
      domainValue = domain,
      systemValue = system,
      defaultSuccessMessage = "Concept set updated successfully.",
    } = {}
  ) => {
    toast.update({
      id: "concept-set",
      ...getConceptToastUpdate(results, {
        isError,
        domainValue,
        systemValue,
        defaultSuccessMessage,
      }),
    });
    resetCreateConceptState();
    resetEditConceptState();
  };

  const createConcept = async (file) => {
    toast.show({
      id: "concept-set",
      isLoading: true,
      ariaLoadingLabel: "creating concept set",
      title: "Creating concept set",
      type: "info",
      message: "Uploading and processing data, please wait...",
      autoClose: false,
      withCloseButton: false,
    });
    setIsCreateConceptSubmitting(true);

    const requestBody = {
      domain,
      codeSystem: system,
      csvFile: file,
    };

    let results;
    try {
      results = await createConceptApi(requestBody).unwrap();
    } catch (err) {
      updateConceptToast(err, { isError: true });
      return;
    }

    updateConceptToast(results, {
      defaultSuccessMessage: "Concept set created successfully.",
    });
  };

  const handleCreateConceptSubmit = (data) => {
    const file = data.fileUpload?.[0];
    if (!file || file.size === 0) {
      window.alert("Chosen file is empty- please upload a valid .csv file.");
      return;
    }
    setIsCreateConceptUploading(true);
    createConcept(file);
  };

  // --- Concept edit state and handlers ---
  const [selectedDomain, setSelectedDomain] = useState("");
  const [selectedSystem, setSelectedSystem] = useState("");
  const [isEditConceptSubmitting, setIsEditConceptSubmitting] = useState(false);
  const [isEditConceptUploading, setIsEditConceptUploading] = useState(false);
  const [updateConceptApi] = useUpdateConceptSetMutation();
  const editConceptForm = useForm();

  useGetConceptSetsQuery();

  const conceptSetsData = useSelector(selectSystemDomainValues);

  const domainDropdownValues = useMemo(() => {
    return getConceptDomainDropdownValues(conceptSetsData);
  }, [conceptSetsData]);

  const systemDropdownValues = useMemo(() => {
    return getConceptSystemDropdownValues(conceptSetsData, selectedDomain);
  }, [conceptSetsData, selectedDomain]);

  useEffect(() => {
    if (
      selectedDomain &&
      systemDropdownValues.length === 1 &&
      selectedSystem?.value !== systemDropdownValues[0].value
    ) {
      setSelectedSystem(systemDropdownValues[0]);
    }
  }, [selectedDomain, selectedSystem, systemDropdownValues]);

  const setSystemDropdown = (event) => {
    setSelectedSystem(getSelectedObject(event, systemDropdownValues));
  };

  const setDomainDropdown = (event) => {
    setSelectedDomain(getSelectedObject(event, domainDropdownValues));
    setSelectedSystem(null);
  };

  const resetEditConceptState = () => {
    setSelectedDomain("");
    setSelectedSystem("");
    editConceptForm.reset();
    setIsEditConceptSubmitting(false);
    setIsEditConceptUploading(false);
  };

  const canEditConceptSubmit =
    isEditConceptSetSelectionComplete(selectedDomain, selectedSystem) &&
    !isEditConceptSubmitting;

  const { rows: editConceptSetRows, isFetching: isEditConceptSetFetching } =
    useConceptSetSearchResults(selectedDomain, selectedSystem);

  // --- Concept view state and handlers ---
  const [viewSelectedDomain, setViewSelectedDomain] = useState("");
  const [viewSelectedSystem, setViewSelectedSystem] = useState("");

  const viewSystemDropdownValues = useMemo(() => {
    return getConceptSystemDropdownValues(conceptSetsData, viewSelectedDomain);
  }, [conceptSetsData, viewSelectedDomain]);

  useEffect(() => {
    if (
      viewSelectedDomain &&
      viewSystemDropdownValues.length === 1 &&
      viewSelectedSystem?.value !== viewSystemDropdownValues[0].value
    ) {
      setViewSelectedSystem(viewSystemDropdownValues[0]);
    }
  }, [viewSelectedDomain, viewSelectedSystem, viewSystemDropdownValues]);

  const setViewDomainDropdown = (event) => {
    setViewSelectedDomain(getSelectedObject(event, domainDropdownValues));
    setViewSelectedSystem(null);
  };

  const setViewSystemDropdown = (event) => {
    setViewSelectedSystem(getSelectedObject(event, viewSystemDropdownValues));
  };

  const downloadConceptSetCsv = (
    rows,
    domainValue,
    systemLabel,
    filenamePrefix = "concept-sets"
  ) => {
    downloadCsv({
      columns: DOWNLOAD_CONCEPT_SET_COLUMNS,
      data: buildConceptSetDownloadRows(rows, domainValue),
      filename: `${filenamePrefix}-${systemLabel}-${domainValue}`,
      columnAccessorKey: "accessorKey",
    });
  };

  const handleDownloadViewConceptSet = () =>
    downloadConceptSetCsv(
      viewConceptSetRows,
      viewSelectedDomain.value,
      viewSelectedSystem.label
    );

  const handleDownloadEditConceptSet = () =>
    downloadConceptSetCsv(
      editConceptSetRows,
      selectedDomain.value,
      selectedSystem.label
    );

  const handleDownloadEditConceptTemplate = () =>
    downloadConceptSetCsv(
      [],
      selectedDomain.value,
      selectedSystem.label,
      "concept-set-template"
    );

  const handleDownloadCreateConceptTemplate = () =>
    downloadConceptSetCsv(
      [],
      domain?.trim(),
      system?.trim(),
      "concept-set-template"
    );

  const {
    condition: viewCondition,
    rows: viewConceptSetRows,
    isFetching: isViewConceptSetFetching,
  } = useConceptSetSearchResults(viewSelectedDomain, viewSelectedSystem);

  const [viewPagination, setViewPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const viewDataTableProps = useDataTable({
    initialData: [],
    initialColumns: VIEW_CONCEPT_SET_COLUMNS,
    tableConfig: {
      enableColumnFilters: true,
      enableSorting: true,
      state: { pagination: viewPagination },
      onPaginationChange: setViewPagination,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [10, 25, 50],
    },
  });

  useEffect(() => {
    if (typeof viewDataTableProps.setData === "function") {
      viewDataTableProps.setData(viewConceptSetRows, true);
    }
  }, [viewConceptSetRows, viewDataTableProps]);

  const editConcept = async (file) => {
    toast.show({
      id: "concept-set",
      isLoading: true,
      ariaLoadingLabel: "editing concept set",
      title: "Editing concept set",
      type: "info",
      message: "Uploading and processing data, please wait...",
      autoClose: false,
      withCloseButton: false,
    });

    setIsEditConceptSubmitting(true);

    const requestBody = buildEditConceptSetUpdateRequest(
      file,
      selectedDomain,
      selectedSystem
    );

    let results;
    try {
      results = await updateConceptApi(requestBody).unwrap();
    } catch (err) {
      updateConceptToast(err, {
        isError: true,
        domainValue: selectedDomain.value,
        systemValue: selectedSystem.label,
      });
      return;
    }
    updateConceptToast(results, {
      domainValue: selectedDomain.value,
      systemValue: selectedSystem.label,
    });
  };

  const handleEditConceptSubmit = (data) => {
    if (!isEditConceptSetSelectionComplete(selectedDomain, selectedSystem)) {
      toast.show({
        id: "concept-set-validation",
        title: "Missing concept set selection",
        type: "error",
        message:
          "Select a domain and system before uploading concept definitions.",
        autoClose: false,
      });
      return;
    }

    const file = data.fileUpload?.[0];

    if (!file || file.size === 0) {
      window.alert("Chosen file is empty- please upload a valid .csv file.");
      return;
    }
    setIsEditConceptUploading(true);
    editConcept(file);
  };

  if (!isAdmin) {
    return (
      <Layout>
        <PageBodyIntro
          title="Administration"
          breadcrumbs={[
            { label: PATHS.admin.displayName, href: PATHS.admin.url },
          ]}
        />
        <RouteDisplay pageRouteDisplay={[PATHS.admin]} />
        <PageBody>
          <Card className="p-4 mt-3">
            <Text fontSize="lg" fontWeight="bold" asElement="div">
              This account has insufficient permission to view this page.
            </Text>
          </Card>
        </PageBody>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageBodyIntro
        title="Administration"
        breadcrumbs={[
          { label: PATHS.admin.displayName, href: PATHS.admin.url },
        ]}
      />
      <RouteDisplay pageRouteDisplay={[PATHS.admin]} />
      <PageBody>
        <Tabs title="Administration">
          <Tabs.Tab label="Create Client">
            <div className="row mt-3 justify-content-center">
              <div className="col-12 col-lg-8">
                <Card className="p-4">
                  <Text fontSize="lg" fontWeight="bold" asElement="div">
                    Create New Client
                  </Text>
                  <Text className="mb-3" asElement="div">
                    Enter the client details below and submit the form.
                  </Text>
                  <form onSubmit={handleCreateClientSubmit} noValidate>
                    <div className="mb-3">
                      <TextInput
                        id="create-client-name"
                        label="Client Name"
                        placeholder="Client Name"
                        value={createClientFormValues.clientName}
                        isDisabled={isCreateClientSubmitting}
                        onChange={handleCreateClientChange("clientName")}
                        model="lowercase"
                        formatter={(value) => {
                          return value.toLowerCase();
                        }}
                      />
                      {createClientErrors.clientName ? (
                        <p className="text-danger small mt-1 mb-0">
                          {createClientErrors.clientName}
                        </p>
                      ) : null}
                    </div>
                    <div className="mb-4">
                      <TextInput
                        id="create-client-description"
                        label="Description (Optional)"
                        placeholder="Brief description"
                        value={createClientFormValues.description}
                        isDisabled={isCreateClientSubmitting}
                        onChange={handleCreateClientChange("description")}
                      />
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        type="submit"
                        isDisabled={!canCreateClientSubmit}
                        isLoading={isCreateClientSubmitting}
                        ariaLoadingLabel="Creating Client"
                      >
                        Create Client
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={resetCreateClientForm}
                        isDisabled={isCreateClientSubmitting}
                      >
                        Clear
                      </Button>
                    </div>
                  </form>
                  {createClientSubmissionState.message ? (
                    <div
                      className={`alert mt-3 ${
                        createClientSubmissionState.variant === "success"
                          ? "alert-success"
                          : "alert-danger"
                      }`}
                      role={
                        createClientSubmissionState.variant === "success"
                          ? "status"
                          : "alert"
                      }
                    >
                      {createClientSubmissionState.message}
                    </div>
                  ) : null}
                </Card>
              </div>
            </div>
          </Tabs.Tab>
          <Tabs.Tab label="Edit Client">
            <div className="row mt-3  justify-content-center">
              <div className="col-12 col-lg-8">
                <Card className="p-4">
                  <Text fontSize="lg" fontWeight="bold" asElement="div">
                    Edit Existing Client
                  </Text>
                  <Text className="mb-3" asElement="div">
                    Select a client and update one or more fields.
                  </Text>
                  <form onSubmit={handleEditClientSubmit} noValidate>
                    <div className="mb-3">
                      <SelectInput
                        id="edit-current-client-id"
                        label="Current Client Name"
                        placeholder={
                          isClientListLoading || isClientListFetching
                            ? "Loading clients..."
                            : "Select..."
                        }
                        options={clientDropdownValues}
                        value={editClientFormValues.selectedClientName}
                        isDisabled={isClientDropdownDisabled}
                        onChange={handleEditClientSelect}
                      />
                      {editClientErrors.selectedClientName ? (
                        <p className="text-danger small mt-1 mb-0">
                          {editClientErrors.selectedClientName}
                        </p>
                      ) : null}
                      {isClientListError ? (
                        <p className="text-danger small mt-1 mb-0">
                          Unable to load clients. Please try again later.
                        </p>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Checkbox
                        label="Retire Client"
                        isChecked={isRetireClient}
                        onChange={handleRetireClientChange}
                        isDisabled={isEditClientSubmitting}
                      />
                    </div>
                    <div className="mb-3">
                      <TextInput
                        id="edit-client-name"
                        label="New Client Name"
                        placeholder="Client Name"
                        value={editClientFormValues.clientName}
                        isDisabled={isEditClientSubmitting || isRetireClient}
                        onChange={handleEditClientChange("clientName")}
                        model="lowercase"
                        formatter={(value) => {
                          return value.toLowerCase();
                        }}
                      />
                      {editClientErrors.clientName ? (
                        <p className="text-danger small mt-1 mb-0">
                          {editClientErrors.clientName}
                        </p>
                      ) : null}
                    </div>

                    <div className="mb-4">
                      <TextInput
                        id="edit-client-description"
                        label="Description"
                        placeholder="Brief description"
                        value={editClientFormValues.description}
                        isDisabled={isEditClientSubmitting || isRetireClient}
                        onChange={handleEditClientChange("description")}
                      />
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        type="submit"
                        isDisabled={!canEditClientSubmit}
                        isLoading={isEditClientSubmitting}
                        ariaLoadingLabel="Updating Client"
                      >
                        Save Client Changes
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={resetEditClientForm}
                        isDisabled={isEditClientSubmitting}
                      >
                        Clear
                      </Button>
                    </div>
                  </form>

                  {editClientSubmissionState.message ? (
                    <div
                      className={`alert mt-3 ${
                        editClientSubmissionState.variant === "success"
                          ? "alert-success"
                          : "alert-danger"
                      }`}
                      role={
                        editClientSubmissionState.variant === "success"
                          ? "status"
                          : "alert"
                      }
                    >
                      {editClientSubmissionState.message}
                    </div>
                  ) : null}
                </Card>
              </div>
            </div>
          </Tabs.Tab>
          <Tabs.Tab label="Create Concept Set">
            <FormProvider
              state={createConceptForm}
              onSubmit={handleCreateConceptSubmit}
            >
              <div className="row mt-3 justify-content-center">
                <div className="col-12 col-lg-8">
                  <Card className="p-4">
                    <Text fontSize="lg" fontWeight="bold" asElement="div">
                      Create New Concept Set
                    </Text>
                    <Text className="mb-3" asElement="div">
                      Input a Domain and System, then upload a file containing
                      Concept Definitions, to create a new Concept.
                    </Text>
                    <div className="row">
                      <div className="mt-auto w-50" style={{ padding: "15px" }}>
                        <label>Domain</label>
                        <TextInput
                          placeholder="Domain"
                          label="Domain"
                          value={domain}
                          isDisabled={isCreateConceptSubmitting}
                          onChange={(event) => setDomain(event.target.value)}
                        />
                      </div>
                      <div className="mt-auto w-50" style={{ padding: "15px" }}>
                        <label>System</label>
                        <TextInput
                          placeholder="System"
                          label="System"
                          value={system}
                          isDisabled={isCreateConceptSubmitting}
                          onChange={(event) => setSystem(event.target.value)}
                        />
                      </div>
                    </div>
                    <div className="d-flex gap-2 mb-3 mt-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleDownloadCreateConceptTemplate}
                        isDisabled={!domain.trim() || !system.trim()}
                      >
                        Download Template
                      </Button>
                    </div>
                    <div className="row">
                      <div style={{ padding: "15px" }}>
                        <label>Concept Definitions</label>
                        <FileUpload
                          maxFiles={1}
                          uploadMessage="Drag your .csv file here"
                          fileTypes={conceptFileTypesAccepted}
                          model="fileUpload"
                          isUploading={isCreateConceptUploading}
                          label="Concept Definitions"
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-left mt-4">
                      <div className="d-flex flex-grow-1">
                        <Button
                          type="submit"
                          isLoading={isCreateConceptSubmitting}
                          ariaLoadingLabel="Creating Concept"
                        >
                          Submit
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </FormProvider>
          </Tabs.Tab>
          <Tabs.Tab label="Edit Concept Set">
            <FormProvider
              state={editConceptForm}
              onSubmit={handleEditConceptSubmit}
            >
              <div className="row mt-3 justify-content-center">
                <div className="col-12 col-lg-8">
                  <Card className="p-4">
                    <Text fontSize="lg" fontWeight="bold" asElement="div">
                      Edit Existing Concept Set
                    </Text>
                    <Text className="mb-3" asElement="div">
                      Select a Domain and System, then upload updated Concept
                      Definitions.
                    </Text>
                    <div className="row">
                      <div className="mt-auto w-50">
                        <label>Domain</label>
                        <SelectInput
                          placeholder="Domain"
                          isSearchable
                          label="Domain"
                          value={selectedDomain ? selectedDomain.value : null}
                          onChange={setDomainDropdown}
                          options={domainDropdownValues}
                        />
                      </div>
                      <div className="mt-auto w-50">
                        <label>System</label>
                        <SelectInput
                          placeholder="System"
                          isSearchable
                          label="System"
                          value={selectedSystem ? selectedSystem.value : null}
                          onChange={setSystemDropdown}
                          options={systemDropdownValues}
                        />
                      </div>
                    </div>
                    <div className="d-flex gap-2 mb-3 mt-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleDownloadEditConceptSet}
                        isDisabled={
                          isEditConceptSetFetching ||
                          editConceptSetRows.length === 0
                        }
                      >
                        Download Existing CSV
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleDownloadEditConceptTemplate}
                        isDisabled={
                          !isEditConceptSetSelectionComplete(
                            selectedDomain,
                            selectedSystem
                          )
                        }
                      >
                        Download Template
                      </Button>
                    </div>
                    <div className="row">
                      <div style={{ padding: "15px" }}>
                        <label>Concept Definitions</label>
                        <FileUpload
                          fileTypes={conceptFileTypesAccepted}
                          isUploading={isEditConceptUploading}
                          maxFiles={1}
                          model="fileUpload"
                          uploadMessage="Drag your .csv file here"
                          label="Concept Definitions"
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-left mt-4">
                      <div className="d-flex flex-grow-1">
                        <Button
                          ariaLoadingLabel="Edit Concept"
                          isDisabled={!canEditConceptSubmit}
                          isLoading={isEditConceptSubmitting}
                          type="submit"
                        >
                          Submit
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </FormProvider>
          </Tabs.Tab>
          <Tabs.Tab label="View Concept Sets">
            <div className="row mt-3 justify-content-center">
              <div className="col-12">
                <Card className="p-4">
                  <Text fontSize="lg" fontWeight="bold" asElement="div">
                    View Concept Set
                  </Text>
                  <Text className="mb-3" asElement="div">
                    Select a Domain and System to view the corresponding Concept
                    Set.
                  </Text>
                  <div className="row mb-4">
                    <div className="mt-auto w-50">
                      <label htmlFor="view-concept-domain">Domain</label>
                      <SelectInput
                        id="view-concept-domain"
                        placeholder="Domain"
                        isSearchable
                        label="Domain"
                        value={
                          viewSelectedDomain ? viewSelectedDomain.value : null
                        }
                        onChange={setViewDomainDropdown}
                        options={domainDropdownValues}
                      />
                    </div>
                    <div className="mt-auto w-50">
                      <label htmlFor="view-concept-system">System</label>
                      <SelectInput
                        id="view-concept-system"
                        placeholder="System"
                        isSearchable
                        label="System"
                        value={
                          viewSelectedSystem ? viewSelectedSystem.value : null
                        }
                        onChange={setViewSystemDropdown}
                        options={viewSystemDropdownValues}
                      />
                    </div>
                  </div>
                  {viewCondition && (
                    <DataTable
                      title="Concept Sets"
                      tableState={viewDataTableProps}
                    >
                      <DataTable.SlotWrapper>
                        <Button
                          onClick={handleDownloadViewConceptSet}
                          isDisabled={
                            isViewConceptSetFetching ||
                            viewConceptSetRows.length === 0
                          }
                          variant="outline"
                        >
                          Download CSV
                        </Button>
                      </DataTable.SlotWrapper>
                      <DataTable.Table isLoading={isViewConceptSetFetching} />
                      <div className="ps-2">
                        <DataTable.Pagination showPageSizeDropdown />
                      </div>
                    </DataTable>
                  )}
                </Card>
              </div>
            </div>
          </Tabs.Tab>
          <Tabs.Tab label="Feature Settings">
            <div className="row mt-3 justify-content-center">
              <div className="col-12 col-lg-8">
                <Card className="p-4">
                  <Text fontSize="lg" fontWeight="bold" asElement="div">
                    Feature Settings
                  </Text>

                  <Text className="mb-3" asElement="div">
                    There are currently no experimental features available.
                  </Text>
                </Card>
              </div>
            </div>
          </Tabs.Tab>
        </Tabs>
      </PageBody>
    </Layout>
  );
}
